// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportListActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { withHandlers } from 'recompose';
import { curry } from 'lodash/fp';
import { getReportType, TYPE_ACCESS_REPORT, TYPE_ACCESS_DASHBOARD } from 'logic/data/Izenda.ImportConstants';
import DetailFilePopup from './Popup/Izenda.DetailFilePopup';

export default withHandlers({
  getType: curry(getType),
  updateCategory: curry(updateCategory),
  updateName: curry(updateName),
  updateSubCategory: curry(updateSubCategory),
  updateSaveIntoId: curry(updateSaveIntoId),
  removeFile: curry(removeFile),
  openDetailFilePopup: curry(openDetailFilePopup)
});

/**
 * Get type
 * @param {Object} props
 * @param {Object} data
 */
function getType(props: any, data: any) {
  if (data.isReport) {
    return getReportType(data.content.type);
  } else {
    return props.localization.getText('COMMON_LABEL_DASHBOARD');
  }
}

/**
 * Update name
 * @param {Object} props
 * @param {Object} importFileContent
 * @param {Object} event
 */
function updateName(props: any, importFileContent: any, event: any) {
  props.store.updateName(importFileContent, event.target.value);
  updateErrorMessages(props);
}

/**
 * Update save into id
 * @param {Object} props
 * @param {Object} importFileContent
 * @param {String} saveIntoId
 */
function updateSaveIntoId(props: any, importFileContent: any, saveIntoId: string) {
  props.store.updateSaveIntoId(importFileContent, saveIntoId);
  updateErrorMessages(props);
}

/**
 * Update category
 * @param {Object} props
 * @param {Object} importFileContent
 * @param {String} categoryId
 */
function updateCategory(props: any, importFileContent: any, categoryId: string, categoryName: string) {
  props.store.updateCategory(importFileContent, categoryId, categoryName);
  updateErrorMessages(props);
}

/**
 * Update sub category
 * @param {Object} props
 * @param {Object} importFileContent
 * @param {String} subCategoryId
 */
function updateSubCategory(props: any, importFileContent: any, subCategoryId: string, subCategoryName: string) {
  props.store.updateSubCategory(importFileContent, subCategoryId, subCategoryName);
  updateErrorMessages(props);
}

/**
 * Update error messages
 * @param {Object} data
 */
function updateErrorMessages({ validation, store }) {
  validation.reset();
  validation.updateErrors(store.errorMessages);
}

/**
 * Remove file
 * @param {Object} props
 * @param {Object} data
 */
function removeFile(props: any, { id, name, content: { index } }): void {
  const { store, validation, isDashboardTab } = props;
  props
    .confirmFn('Information', {
      description: String.format(props.localization.getText('IMPORT_DELETE_MESSAGE'), name),
      type: 'warning',
      showCloseIndicator: false
    })
    .then(() => {
      store.removeFiles([id]).then((isSuccess: boolean) => {
        if (isSuccess) {
          isDashboardTab ? store.updateDashboards(id, index) : store.updateReports(id, index);
          store.updateDatabaseMappings().then(() => {
            if (
              (store.hasErrorOnClientSideReportTab && store.model.activeTabKey === 'report') ||
              (store.hasErrorOnClientSideDashboardTab && store.model.activeTabKey === 'dashboard')
            ) {
              validation.validate().then(status => {
                status && updateErrorMessages(props);
              });
            } else {
              updateErrorMessages(props);
            }
          });
        }
      });
    });
}

/**
 * Get detail file then open detail file popup
 * @param {Object} props
 * @param {Object} report
 */
function openDetailFilePopup(props: any, data: any) {
  const { store } = props;
  const fileInfo = store.getFileInfo(data);
  const isReport = data.isReport;
  const typeAccess = isReport ? TYPE_ACCESS_REPORT : TYPE_ACCESS_DASHBOARD;
  const title = getTitleDetailPopup(props, data);

  store.getDetailFile(fileInfo, typeAccess).then(result => {
    const [content, loadAccessRights] = result;

    props.openPopup(DetailFilePopup, {
      title,
      content,
      loadAccessRights
    });
  });
}

/**
 * Get title popup
 * @param {Object} data
 * @param {Object} props
 * @returns {String}
 */
function getTitleDetailPopup(props: any, data: any): string {
  const { localization } = props;
  if (data.isReport) {
    if (data.content.type === 0) {
      return localization.getText('IMPORT_REPORT_INFORMATION');
    } else {
      return localization.getText('IMPORT_TEMPLATE_INFORMATION');
    }
  } else {
    return localization.getText('IMPORT_DASHBOARD_INFORMATION');
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Import/Izenda.ImportListActions.js